import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  IAccount,
  IAccountDetail,
  IAccountBalance,
  IAccountMovement,
  IAccountLimits,
  IAccountMovementDetail,
  IAccountBlockingReason,
  ICertificateRequest,
  IAccountStatementRequest,
  IAccountStatementResponse,
  IApplyGmf,
  IBlockAccountParams,
  ICancelAccountParams,
  IGmfParams,
  IGmfPJParams,
  IAccountBlockingType,
  IAccountBlockingReasonRole,
  IBlockingReasonsResponse,
  IAccountFreeze,
  ICertificateDownloadRequest,
  ICertificateDownloadResponse,
  ICertificateYearsResponse,
  IBreBKeysResponse,
} from '@app/shared/models/banking.model';
import { ICollection, IDtoResponse } from '@app/shared/models/dtos/response.dto';
import { BlockReasonParam, BlockReasonTypeParam, CollectionParam, PocketVaultParam } from '@app/shared/models/param.model';
import { APP_CONFIG } from '@shared/config/app.config';
import { BehaviorSubject, map, Observable, of, delay, throwError } from 'rxjs';
import { ServiceBase } from '../base/base.service';
import { IPockets } from '@app/shared/models/pockets.model';
import { ACCOUNT_STATUS, PARAM_BLOCK_REASON } from '@app/shared/types/enums';
import { PATH } from '@app/shared/types/enums/path';
import { ActionEnum } from '@app/shared/types/enums/ActionEnum.enum';
import { AppContext } from '../context/app.context.service';

@Injectable({
  providedIn: 'root',
})
export class AccountBasicService extends ServiceBase {
  public accountInfo$: BehaviorSubject<IAccountDetail> = new BehaviorSubject<IAccountDetail>({} as IAccountDetail);

  constructor(public http: HttpClient, private readonly _appCtx: AppContext) {
    super(`${APP_CONFIG.serviceBaseUrl}/banking/api/v1/account-basic`);
  }

  get accountInfo(): IAccountDetail {
    return this.accountInfo$.getValue() ?? ({} as IAccountDetail);
  }

  set accountInfo(value: IAccountDetail) {
    this.accountInfo$.next(value);
  }

  getAccounts(rppUserId: string, status?: string): Observable<IAccount[] | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ACCOUNT).set('monitoring_action', ActionEnum.READ);

    return this.http
      .get<IDtoResponse<ICollection<IAccount>>>(`${this.baseUrl}/${rppUserId}/accounts`, {
        params: new HttpParams().set('status', status || ''),
        headers,
      })
      .pipe(
        map(result => {
          result.data?.items.sort((a, b) => {
            if (a.status === ACCOUNT_STATUS.ENABLE && b.status !== ACCOUNT_STATUS.ENABLE) {
              return -1;
            } else if (a.status !== ACCOUNT_STATUS.ENABLE && b.status === ACCOUNT_STATUS.ENABLE) {
              return 1;
            } else {
              return 0;
            }
          });

          return result.data?.items;
        })
      );
  }

  getAccountBlocks(rppUserId: string): Observable<IBlockingReasonsResponse[] | undefined> {
    return this.http.get<IDtoResponse<IBlockingReasonsResponse[]>>(`${this.baseUrl}/${rppUserId}/block/list`).pipe(
      map(result => {
        return result.data;
      })
    );
  }

  getAccountDetail(rppUserId: string, accountCode: string): Observable<IAccountDetail | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ACCOUNT).set('monitoring_action', ActionEnum.READ);

    return this.http
      .get<IDtoResponse<IAccountDetail>>(`${this.baseUrl}/${rppUserId}/account/${accountCode}/detail`, { headers })
      .pipe(map(res => res.data));
  }

  getAccountMovements(
    rppUserId: string,
    accountCode: string,
    params: CollectionParam
  ): Observable<ICollection<IAccountMovement> | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ACCOUNT).set('monitoring_action', ActionEnum.READ);

    return this.http
      .get<IDtoResponse<ICollection<IAccountMovement>>>(`${this.baseUrl}/${rppUserId}/account/${accountCode}/movements`, {
        params: new HttpParams().set('pageIndex', params.pageIndex.toString()).set('pageSize', params.pageSize.toString()),
        headers,
      })
      .pipe(map(res => res.data));
  }

  getAccountLimits(rppUserId: string, accountCode: string): Observable<IAccountLimits | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ACCOUNT).set('monitoring_action', ActionEnum.READ);

    return this.http
      .get<IDtoResponse<IAccountLimits>>(`${this.baseUrl}/${rppUserId}/account/${accountCode}/limits`, {
        headers,
      })
      .pipe(map(res => res.data));
  }

  getAccountMovementDetail(rppUserId: string, accountCode: string, idMovement: string): Observable<IAccountMovementDetail | undefined> {
    const headers = new HttpHeaders()
      .set('timezone', 'UTC-0')
      .set('monitoring_view', PATH.ACCOUNT)
      .set('monitoring_action', ActionEnum.READ);

    return this.http
      .get<IDtoResponse<IAccountMovementDetail>>(`${this.baseUrl}/${rppUserId}/account/${accountCode}/movement/${idMovement}`, {
        headers,
      })
      .pipe(map(response => response.data));
  }

  /** deprecate 13-06-2024*/
  getAccountBalance(rppUserId: string, accountCode: string): Observable<IAccountBalance | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.WIDGETS).set('monitoring_action', ActionEnum.READ);

    return this.http
      .get<IDtoResponse<IAccountBalance>>(`${this.baseUrl}/${rppUserId}/account/${accountCode}/balance`, { headers })
      .pipe(map(res => res.data));
  }

  // getAccountBalanceWasTransfered(rppUserId: string): Observable<IAccountBalanceTransfered | undefined> {
  //   return this.http
  //     .get<IDtoResponse<IAccountBalanceTransfered>>(`${this.baseUrl}/${rppUserId}/account/balance`)
  //     .pipe(map(res => res.data));
  // }

  getBlockingReasons(rppUserId: string, accountStatus: string): Observable<IDtoResponse<IAccountBlockingReason[]>> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ACCOUNT).set('monitoring_action', ActionEnum.READ);

    return this.http.get<IDtoResponse<IAccountBlockingReason[]>>(`${this.baseUrl}/${rppUserId}/account/${accountStatus}/reasons`, {
      headers,
    });
  }

  getBlockingReasonsRole(params: BlockReasonTypeParam): Observable<IDtoResponse<IAccountBlockingReasonRole>> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ACCOUNT).set('monitoring_action', ActionEnum.READ);

    return this.http.get<IDtoResponse<IAccountBlockingReasonRole>>(`${APP_CONFIG.serviceBaseUrl}/wallet/api/v1/blocking/v2/scope/rules`, {
      headers,
      params: new HttpParams()
        .set(PARAM_BLOCK_REASON.SCOPE, params.scope)
        .set(PARAM_BLOCK_REASON.ACCOUNT_ORIGIN, params.accountCode)
        .set(PARAM_BLOCK_REASON.ACTION, params.action),
    });
  }

  sendCertificate(
    rppUserId: string,
    accountCode: string,
    certificate: ICertificateRequest
  ): Observable<IDtoResponse<IAccountBlockingReason[]>> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ACCOUNT).set('monitoring_action', ActionEnum.READ);

    return this.http.put<IDtoResponse<IAccountBlockingReason[]>>(
      `${this.baseUrl}/${rppUserId}/account/${accountCode}/send-certificate`,
      certificate,
      {
        headers,
      }
    );
  }

  getCertificateYears(rppUserId: string, country: string, documentType: string): Observable<number[]> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ACCOUNT).set('monitoring_action', ActionEnum.READ);

    return this.http
      .get<IDtoResponse<ICertificateYearsResponse>>(`${this.baseUrl}/${rppUserId}/${country}/certificates/availability`, {
        headers,
        params: { document_type: documentType },
      })
      .pipe(map(res => res.data?.years || []));
  }

  downloadCertificate(rppUserId: string, request: ICertificateDownloadRequest): Observable<ICertificateDownloadResponse | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ACCOUNT).set('monitoring_action', ActionEnum.READ);

    return this.http
      .post<IDtoResponse<ICertificateDownloadResponse>>(`${this.baseUrl}/${rppUserId}/certificates/download`, request, {
        headers,
      })
      .pipe(map(res => res.data));
  }

  getAccountStatement(
    rppUserId: string,
    accountCode: string,
    accStatementReq: IAccountStatementRequest
  ): Observable<IAccountStatementResponse | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ACCOUNT).set('monitoring_action', ActionEnum.READ);

    const params = new HttpParams({
      fromObject: {
        ...accStatementReq,
      },
    });

    return this.http
      .get<IDtoResponse<IAccountStatementResponse>>(`${this.baseUrl}/${rppUserId}/account/${accountCode}/statement`, {
        params,
        headers,
      })
      .pipe(map(res => res.data));
  }

  setGmf(setGmfParams: IGmfParams): Observable<IApplyGmf | undefined> {
    const { rppUserId, accountCode, isGmf } = setGmfParams;

    const headers = new HttpHeaders()
      .set('agent_email', setGmfParams.agent_email?.toString() || '')
      .set('monitoring_view', PATH.ACCOUNT)
      .set('monitoring_action', ActionEnum.UPDATE);

    return this.http
      .put<IDtoResponse<IApplyGmf>>(
        `${this.baseUrl}/${rppUserId}/account/${accountCode}/action/gmf`,
        {},
        {
          headers,
          params: new HttpParams().set('isGmf', isGmf.toString()),
        }
      )
      .pipe(map(res => res.data));
  }

  setGmfPJ(setGmfPJParams: IGmfPJParams): Observable<IApplyGmf | undefined> {
    const { rppUserId, accountCode, isGmf, gmfReason } = setGmfPJParams;

    const headers = new HttpHeaders()
      .set('agent_email', setGmfPJParams.agent_email?.toString() || '')
      .set('monitoring_view', PATH.ACCOUNT)
      .set('monitoring_action', ActionEnum.UPDATE);

    return this.http
      .put<IDtoResponse<IApplyGmf>>(
        `${this.baseUrl}/${rppUserId}/account/${accountCode}/action/gmf`,
        {},
        {
          headers,
          params: new HttpParams().set('isGmf', isGmf.toString()).set('gmfReason', gmfReason),
        }
      )
      .pipe(map(res => res.data));
  }

  blockAccount(blockAccount: IBlockAccountParams): Observable<IDtoResponse<IAccountBlockingReason[]>> {
    const { rppUserId, accountCode, reasonId, reasonDescription, blockType, agent_email, areaId, action } = blockAccount;

    const requestBody = {
      reasonId: reasonId,
      blockType: blockType,
      areaId: areaId,
      reasonDescription: reasonDescription,
    };

    const headers = new HttpHeaders()
      .set('agent_email', agent_email?.toString() || '')
      .set('monitoring_view', PATH.ACCOUNT)
      .set('monitoring_action', ActionEnum.UPDATE);

    return this.http
      .put<IDtoResponse<IAccountBlockingReason[]>>(`${this.baseUrl}/${rppUserId}/v2/account/${accountCode}/action/${action}`, requestBody, {
        headers,
      })
      .pipe(map(res => res));
  }

  cancelAccount(cancelAccount: ICancelAccountParams): Observable<IDtoResponse<any>> {
    const { rppUserId, accountCode, reasonId, action, userId, agent_email } = cancelAccount;
    const requestBody = {
      action: action,
      reasonId: reasonId,
    };

    const headers = new HttpHeaders()
      .set('agent_email', agent_email?.toString() || '')
      .set('monitoring_view', PATH.ACCOUNT)
      .set('monitoring_action', ActionEnum.UPDATE);

    return this.http
      .put<IDtoResponse<any>>(`${this.baseUrl}/${rppUserId}/user/${userId}/account/${accountCode}/action/cancel`, requestBody, { headers })
      .pipe(map(res => res.data));
  }

  getPocketsList(rppUserId: string, accountCode: string, params: PocketVaultParam): Observable<ICollection<IPockets> | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ACCOUNT).set('monitoring_action', ActionEnum.READ);

    return this.http
      .get<IDtoResponse<ICollection<IPockets>>>(`${this.baseUrl}/${rppUserId}/account/${accountCode}/pockets`, {
        params: new HttpParams()
          .set('pageIndex', params.pageIndex.toString())
          .set('pageSize', params.pageSize.toString())
          .set('types', params.type)
          .set('status', params.status),
      })
      .pipe(map(res => res.data));
  }

  getAccountFreeze(rppUserId: string, accountCode: string): Observable<IDtoResponse<IAccountFreeze[]>> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ACCOUNT).set('monitoring_action', ActionEnum.READ);

    return this.http
      .get<IDtoResponse<IAccountFreeze[]>>(`${this.baseUrl}/${rppUserId}/account/${accountCode}/freeze`, {
        headers,
      })
      .pipe(map(res => res));
  }

  getBreBKeys(rppUserId: string, accountCode: string): Observable<IBreBKeysResponse | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ACCOUNT).set('monitoring_action', ActionEnum.READ);

    return this.http
      .get<IDtoResponse<IBreBKeysResponse>>(`${this.baseUrl}/${rppUserId}/account/${accountCode}/bre-b-keys`, {
        headers,
      })
      .pipe(map(res => res.data));
  }
}
